// router/linkInterceptor.ts - Link Click Interception

import { isClientSideRoute } from "./routes";
import { navigateTo, isExternalUrl } from "./navigation";

/**
 * Set up link click interceptor for client-side navigation
 * Only intercepts links that have corresponding client-side routes
 */
export function setupLinkInterceptor(): void {
	document.addEventListener("click", handleLinkClick);
}

/**
 * Handle link clicks and intercept for client-side routing
 * @param event - The click event
 */
async function handleLinkClick(event: MouseEvent): Promise<void> {
	const target = event.target as Node;

	// Find the closest 'a' tag (click may be on text inside the link)
	const el = target.nodeType === Node.TEXT_NODE ? (target as Text).parentElement : (target as HTMLElement);
	const link = el?.closest?.("a") ?? null;

	// If it's not a link, or if it's a link with no real href, bail out
	if (!link || link.getAttribute("href") === "#") {
		return;
	}

	// Don't intercept if:
	// 1. It's meant to open in a new tab/window
	// 2. Modifier keys are pressed (Ctrl/Cmd/Shift for opening in new tab)
	// 3. It has download attribute
	// 4. It's a mailto: or tel: link
	if (
		link.target === "_blank" ||
		event.metaKey ||
		event.ctrlKey ||
		event.shiftKey ||
		link.hasAttribute("download") ||
		link.protocol === "mailto:" ||
		link.protocol === "tel:"
	) {
		return;
	}

	// Check if it's an external link
	if (isExternalUrl(link.href)) {
		return;
	}

	// Get the path from the link
	const url = new URL(link.href);
	const path = url.pathname;

	// Check if this is just a hash change on the current page
	if (
		url.pathname === window.location.pathname &&
		url.search === window.location.search &&
		url.hash
	) {
		// It's an anchor link on the same page.
		// Prevent default to stop a full reload, but handle scrolling ourselves.
		event.preventDefault();

		// Manually scroll to the element.
		const element = document.querySelector(url.hash);
		if (element) {
			element.scrollIntoView({ behavior: "smooth" });
		}

		// Update the URL in the history without triggering a full navigation.
		window.history.pushState({}, "", url.href);

		return; // Stop further processing.
	}

	// Only intercept if this route is handled client-side
	if (!isClientSideRoute(path)) {
		console.log(
			`Link to ${path} not handled by client-side router, using server navigation`
		);
		return;
	}

	// It's an internal link with a client-side route!
	// Prevent the default page reload
	event.preventDefault();

	// Navigate using client-side router (use pathname + search + hash)
	const fullPath = url.pathname + url.search + url.hash;
	await navigateTo(fullPath);
}
